/**
 * Input Validation and XSS Protection Utilities
 * Provides comprehensive input sanitization and validation for form components
 */

// XSS Protection - Remove potentially dangerous characters and scripts
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/[<>]/g, '') // Remove < and > to prevent script tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/vbscript:/gi, '') // Remove vbscript: protocol
    .trim();
};

// Email validation
export const validateEmail = (email) => {
  if (!email) return { isValid: false, message: 'Email is required' };
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const sanitizedEmail = sanitizeInput(email);
  
  if (!emailRegex.test(sanitizedEmail)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }
  
  // Additional security checks
  if (sanitizedEmail.length > 254) {
    return { isValid: false, message: 'Email address is too long' };
  }
  
  return { isValid: true, value: sanitizedEmail };
};

// Phone number validation (10 digits for Indian numbers)
export const validatePhone = (phone) => {
  if (!phone) return { isValid: false, message: 'Phone number is required' };
  
  const phoneRegex = /^[0-9]{10}$/;
  const sanitizedPhone = sanitizeInput(phone.toString());
  
  if (!phoneRegex.test(sanitizedPhone)) {
    return { isValid: false, message: 'Please enter a valid 10-digit phone number' };
  }
  
  return { isValid: true, value: sanitizedPhone };
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name) return { isValid: false, message: `${fieldName} is required` };
  
  const sanitizedName = sanitizeInput(name);
  
  if (sanitizedName.length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters long` };
  }
  
  if (sanitizedName.length > 50) {
    return { isValid: false, message: `${fieldName} must be less than 50 characters` };
  }
  
  // Allow only letters, spaces, and common name characters
  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  if (!nameRegex.test(sanitizedName)) {
    return { isValid: false, message: `${fieldName} contains invalid characters` };
  }
  
  return { isValid: true, value: sanitizedName };
};

// Text validation for general text fields
export const validateText = (text, fieldName = 'Text', minLength = 1, maxLength = 1000) => {
  if (!text && minLength > 0) return { isValid: false, message: `${fieldName} is required` };
  
  const sanitizedText = sanitizeInput(text || '');
  
  if (sanitizedText.length < minLength) {
    return { isValid: false, message: `${fieldName} must be at least ${minLength} character(s) long` };
  }
  
  if (sanitizedText.length > maxLength) {
    return { isValid: false, message: `${fieldName} must be less than ${maxLength} characters` };
  }
  
  return { isValid: true, value: sanitizedText };
};

// Number validation
export const validateNumber = (number, fieldName = 'Number', min = null, max = null) => {
  if (!number && min !== null) return { isValid: false, message: `${fieldName} is required` };
  
  const sanitizedNumber = sanitizeInput(number?.toString() || '');
  
  if (sanitizedNumber && !/^\d+$/.test(sanitizedNumber)) {
    return { isValid: false, message: `${fieldName} must contain only numbers` };
  }
  
  const numValue = parseInt(sanitizedNumber);
  
  if (min !== null && numValue < min) {
    return { isValid: false, message: `${fieldName} must be at least ${min}` };
  }
  
  if (max !== null && numValue > max) {
    return { isValid: false, message: `${fieldName} must be at most ${max}` };
  }
  
  return { isValid: true, value: sanitizedNumber };
};

// URL validation
export const validateUrl = (url, fieldName = 'URL') => {
  if (!url) return { isValid: false, message: `${fieldName} is required` };
  
  const sanitizedUrl = sanitizeInput(url);
  
  try {
    new URL(sanitizedUrl);
    return { isValid: true, value: sanitizedUrl };
  } catch {
    return { isValid: false, message: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
};

// Date validation
export const validateDate = (date, fieldName = 'Date') => {
  if (!date) return { isValid: false, message: `${fieldName} is required` };
  
  const sanitizedDate = sanitizeInput(date);
  const dateObj = new Date(sanitizedDate);
  
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, message: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
  
  return { isValid: true, value: sanitizedDate };
};

// Password validation
export const validatePassword = (password) => {
  if (!password) return { isValid: false, message: 'Password is required' };
  
  const sanitizedPassword = sanitizeInput(password);
  
  if (sanitizedPassword.length < 6) {
    return { isValid: false, message: 'Password must be at least 6 characters long' };
  }
  
  if (sanitizedPassword.length > 128) {
    return { isValid: false, message: 'Password is too long' };
  }
  
  return { isValid: true, value: sanitizedPassword };
};

// PIN validation (6 digits)
export const validatePin = (pin) => {
  if (!pin) return { isValid: false, message: 'PIN is required' };
  
  const sanitizedPin = sanitizeInput(pin.toString());
  
  if (!/^\d{6}$/.test(sanitizedPin)) {
    return { isValid: false, message: 'PIN must be exactly 6 digits' };
  }
  
  return { isValid: true, value: sanitizedPin };
};

// Address validation
export const validateAddress = (address) => {
  if (!address) return { isValid: false, message: 'Address is required' };
  
  const sanitizedAddress = sanitizeInput(address);
  
  if (sanitizedAddress.length < 10) {
    return { isValid: false, message: 'Address must be at least 10 characters long' };
  }
  
  if (sanitizedAddress.length > 500) {
    return { isValid: false, message: 'Address is too long' };
  }
  
  return { isValid: true, value: sanitizedAddress };
};

// Generic input handler with validation
export const createValidatedInputHandler = (validationFn, setValue, setError) => {
  return (e) => {
    const inputValue = e.target.value;
    const validation = validationFn(inputValue);
    
    if (validation.isValid) {
      setValue(validation.value);
      setError('');
    } else {
      setValue(inputValue); // Keep the input value for user to see
      setError(validation.message);
    }
  };
};

// Form submission validation
export const validateForm = (formData, validationRules) => {
  const errors = {};
  const sanitizedData = {};
  
  for (const [field, rule] of Object.entries(validationRules)) {
    const value = formData[field];
    const validation = rule(value, field);
    
    if (!validation.isValid) {
      errors[field] = validation.message;
    } else {
      sanitizedData[field] = validation.value;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedData
  };
};

// Prevent XSS in form submission
export const sanitizeFormData = (formData) => {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}; 